import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { chatbotAdminService } from '@/api/chatbot-admin';
import type { AdminFeedback } from '@/types/chatbot-admin';
import { SimpleTablePagination } from '@/components/simple-table-pagination';
import { Main } from '@/components/layout/main';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { superAdminService } from '@/api/super-admin';
import type { SuperAdminCabinet } from '@/types/super-admin';
import { timestamp_to_date } from '@/utils/time';

export default function ChatbotFeedbackAdmin() {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState<AdminFeedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [cabinet, setCabinet] = useState<string>('');
  const [cabinets, setCabinets] = useState<SuperAdminCabinet[]>([]);

  // Local pagination since feedback endpoint returns array without count in spec
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filtered = useMemo<AdminFeedback[]>(() => {
    const list = Array.isArray(feedback) ? feedback : [];
    const s = search.trim().toLowerCase();
    if (!s) return list;
    return list.filter((f) =>
      (f.user?.name || '').toLowerCase().includes(s) ||
      (f.sent_message || '').toLowerCase().includes(s) ||
      (f.received_message || '').toLowerCase().includes(s) ||
      (f.feedback_type || '').toLowerCase().includes(s)
    );
  }, [feedback, search]);

  const paged = useMemo<AdminFeedback[]>(() => {
    const list = Array.isArray(filtered) ? filtered : [];
    const start = (page - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await chatbotAdminService.getFeedback(cabinet ? { cabinet } : undefined);
        setFeedback(data);
      } catch (e: any) {
        setError(e.message || 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cabinet]);

  useEffect(() => {
    const fetchCabinets = async () => {
      try {
        const data = await superAdminService.getCabinets();
        const list = Array.isArray(data) ? data : (data as any)?.results || [];
        setCabinets(list);
      } catch (err) {
        setCabinets([]);
      }
    };
    fetchCabinets();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  return (
    <>
      <Main>
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('super_admin_feedback')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Input
                  placeholder={t('search_feedback')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Select value={cabinet || 'none'} onValueChange={(v) => setCabinet(v === 'none' ? '' : v)}>
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder={t('cabinet')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('all_cabinets')}</SelectItem>
                    {cabinets.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('user')}</TableHead>
                      <TableHead>{t('sent_message')}</TableHead>
                      <TableHead>{t('received_message')}</TableHead>
                      <TableHead>{t('feedback_type')}</TableHead>
                      <TableHead>{t('created_at')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={5}>{t('loading')}</TableCell>
                      </TableRow>
                    )}
                    {error && !loading && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-destructive">{error}</TableCell>
                      </TableRow>
                    )}
                    {!loading && !error && paged.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>{t('no_feedback_found')}</TableCell>
                      </TableRow>
                    )}
                    {paged.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell>{f.user?.name || '-'}</TableCell>
                        <TableCell className="max-w-[280px] truncate" title={f.sent_message}>{f.sent_message}</TableCell>
                        <TableCell className="max-w-[280px] truncate" title={f.received_message}>{f.received_message}</TableCell>
                        <TableCell>
                          <Badge variant={f.feedback_type === 'positive' ? 'default' : 'secondary'}>
                            {t(f.feedback_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>{f.created_at ? timestamp_to_date(Number(f.created_at)).toLocaleString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-3">
                <SimpleTablePagination
                  totalItems={filtered.length}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}